import type {
  ClasseMarca,
  ClasseSelecionada,
  NivelRisco,
  ProcessoMarca,
  ResultadoLoteItem,
  RiscoMarca,
  SugestaoClasses,
} from './types'

export const INPI_DETAIL_URL = 'https://busca.inpi.gov.br/pePI/servlet/MarcasServletController?Action=detail&CodPedido='

export function linkInpi(codPedido: string): string | null {
  return codPedido ? `${INPI_DETAIL_URL}${codPedido}` : null
}

export function formatarDataHora(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export const NIVEL_LABEL: Record<NivelRisco, string> = {
  alto: 'ALTO',
  medio: 'MÉDIO',
  baixo: 'BAIXO',
}

export const NIVEL_EMOJI: Record<NivelRisco, string> = {
  alto: '🔴',
  medio: '🟡',
  baixo: '🟢',
}

/** "NCL 25" pra Nice; "Antiga 40.10" pra Classificação Nacional (registros anteriores a
 * 2000, que o INPI mostra como "40 : 10") — mesmo rótulo de _rotulo_classe em tools/marca.py.
 * A numeração antiga não é a de Nice, por isso o rótulo é diferente. */
export function rotuloClasse(c: ClasseMarca): string {
  if (c.sistema === 'nacional') {
    const dois = (n: number) => String(n).padStart(2, '0')
    return `Antiga ${dois(c.numero)}.${dois(c.item ?? 0)}`
  }
  return `NCL ${c.numero}`
}

/** "árvore de natal (NCL 28, 16, 21)" — como o produto analisado aparece na tela e nos
 * arquivos exportados. Vazio quando a análise usou a regra geral. */
export function produtoTexto(produto: string | undefined, classes: number[] | undefined): string {
  if (!classes?.length) return ''
  const lista = `NCL ${classes.join(', ')}`
  return produto?.trim() ? `${produto.trim()} (${lista})` : lista
}

/** Transforma a sugestão da IA na seleção da tela: principais primeiro, depois vizinhas. */
export function selecaoDaSugestao(s: Extract<SugestaoClasses, { ok: true }>): ClasseSelecionada[] {
  return [
    ...s.principais.map((numero) => ({ numero, papel: 'principal' as const })),
    ...s.vizinhas.filter((n) => !s.principais.includes(n)).map((numero) => ({ numero, papel: 'vizinha' as const })),
  ]
}

/** Extrai as marcas únicas de um texto colado (uma por linha) — mesmo critério de
 * _parsear_lista_marcas em tools/marca.py: ignora vazias/comentários, dedupe
 * case-insensitive preservando a grafia da primeira ocorrência. */
export function parsearListaMarcas(texto: string): string[] {
  const vistas = new Set<string>()
  const marcas: string[] = []
  for (const linhaRaw of texto.split(/\r?\n/)) {
    const termo = linhaRaw.trim()
    if (!termo || termo.startsWith('#')) continue
    const chave = termo.toLowerCase()
    if (vistas.has(chave)) continue
    vistas.add(chave)
    marcas.push(termo)
  }
  return marcas
}

// --------------------------------------------------------------------- exportação do lote

type Celula = string | number

function rotuloNivel(nivel: NivelRisco | 'erro'): string {
  return nivel === 'erro' ? 'ERRO' : NIVEL_LABEL[nivel]
}

/** "Completa" ou "Parcial (60 de 157 processos)" — coluna dos arquivos exportados. */
export function leituraTexto(risco: RiscoMarca | null): string {
  if (!risco) return ''
  const p = risco.parcial
  if (!p) return 'Completa'
  return p.total ? `Parcial (${p.lidos} de ${p.total} processos)` : `Parcial (${p.lidos} processos)`
}

function classesTexto(p: ProcessoMarca): string {
  return p.classes.length ? p.classes.map(rotuloClasse).join(', ') : 'Classe não informada'
}

/** O Excel, ao abrir um CSV, lê `006733727` como número e come o zero à esquerda.
 * `="..."` força a célula a ficar como texto. */
function textoExcel(valor: string): string {
  return valor ? `="${valor}"` : ''
}

/** Uma linha por marca pesquisada — o mesmo que a lista da tela mostra fechada. */
export function linhasExportResumo(resultados: ResultadoLoteItem[], produtoAnalisado = ''): Celula[][] {
  const aoa: Celula[][] = [
    [
      'Marca',
      'Risco',
      'Leitura',
      'Alto renome',
      'Processos ativos',
      'Processos inativos',
      'Classes em conflito',
      'Titular(es)',
      'Observação',
      'Produto analisado',
    ],
  ]
  for (const { linha, risco } of resultados) {
    if (linha.nivel === 'erro') {
      aoa.push([linha.marca, 'ERRO', '', '', '', '', '', '', linha.erro_msg ?? '', produtoAnalisado])
      continue
    }
    aoa.push([
      linha.marca,
      rotuloNivel(linha.nivel),
      leituraTexto(risco),
      risco?.alto_renome ? 'Sim' : 'Não',
      linha.qtd_ativos,
      linha.qtd_inativos,
      linha.classes_conflito.join(', '),
      linha.titulares.join(' | '),
      [risco?.mensagem, risco?.parcial?.aviso].filter(Boolean).join(' '),
      produtoAnalisado,
    ])
  }
  return aoa
}

/** Uma linha por processo do INPI (ativos e inativos). Marca que deu erro ou não tem
 * processo nenhum entra com uma linha só, pra não sumir do arquivo. */
export function linhasExportProcessos(resultados: ResultadoLoteItem[], produtoAnalisado = ''): Celula[][] {
  const aoa: Celula[][] = [
    [
      'Marca pesquisada',
      'Risco',
      'Leitura',
      'Número',
      'Marca no INPI',
      'Titular',
      'Situação',
      'Ativo',
      'Classes',
      'Link INPI',
      'Produto analisado',
    ],
  ]
  for (const { linha, risco } of resultados) {
    const nivel = rotuloNivel(linha.nivel)
    if (!risco) {
      aoa.push([linha.marca, nivel, '', '', '', '', linha.erro_msg ?? '', '', '', '', produtoAnalisado])
      continue
    }
    if (!risco.ativos.length && !risco.inativos.length) {
      aoa.push([linha.marca, nivel, leituraTexto(risco), '', '', '', risco.mensagem, '', '', '', produtoAnalisado])
      continue
    }
    const grupos: Array<[ProcessoMarca[], string]> = [
      [risco.ativos, 'Sim'],
      [risco.inativos, 'Não'],
    ]
    for (const [processos, ativo] of grupos) {
      for (const p of processos) {
        aoa.push([
          linha.marca,
          nivel,
          leituraTexto(risco),
          textoExcel(p.numero),
          p.marca,
          p.titular,
          p.situacao,
          ativo,
          classesTexto(p),
          linkInpi(p.codPedido) ?? '',
          produtoAnalisado,
        ])
      }
    }
  }
  return aoa
}

export function nomeArquivoLote(tipo: 'resumo' | 'processos', hoje = new Date()): string {
  return `marcas_inpi_${tipo}_${hoje.toISOString().slice(0, 10)}.csv`
}
